import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { ArrowUp, ArrowDown, TrendingUp, DollarSign, ShoppingCart, Award, Package, Users, RefreshCw } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

const ServiceAnalyticsDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [categoryAnalytics, setCategoryAnalytics] = useState(null);
  const [roiMetrics, setRoiMetrics] = useState(null);
  const [trends, setTrends] = useState(null);
  const [country, setCountry] = useState('UK');
  const [selectedCategory, setSelectedCategory] = useState('freelance-gigs');
  const [periodDays, setPeriodDays] = useState(30);
  const [activeTab, setActiveTab] = useState('overview');

  const categories = [
    { id: 'freelance-gigs', name: 'Freelance & Gigs', icon: '💼' },
    { id: 'logistics-transport', name: 'Logistics & Transport', icon: '🚚' },
    { id: 'creative-media', name: 'Creative & Media', icon: '🎨' },
    { id: 'manufacturing-warehouse', name: 'Manufacturing', icon: '🏭' },
    { id: 'construction-property', name: 'Construction', icon: '🏗️' },
    { id: 'hospitality', name: 'Hospitality', icon: '🏨' },
    { id: 'retail', name: 'Retail', icon: '🛍️' },
    { id: 'security', name: 'Security', icon: '🛡️' },
  ];

  const fetchOverview = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/job-services/admin/analytics/overview?country=${country}`);
      const data = await res.json();
      if (data.success) setOverview(data);
    } catch (err) {
      console.error('Error fetching overview:', err);
    }
  }, [country]);

  const fetchCategoryAnalytics = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/job-services/admin/analytics/category/${selectedCategory}?country=${country}&days=${periodDays}`);
      const data = await res.json();
      if (data.success) setCategoryAnalytics(data);
    } catch (err) {
      console.error('Error fetching category analytics:', err);
    }
  }, [selectedCategory, country, periodDays]);

  const fetchRoiMetrics = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/job-services/admin/analytics/roi/${selectedCategory}?country=${country}`);
      const data = await res.json();
      if (data.success) setRoiMetrics(data);
    } catch (err) {
      console.error('Error fetching ROI metrics:', err);
    }
  }, [selectedCategory, country]);

  const fetchTrends = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/job-services/admin/analytics/trends?country=${country}&days=${periodDays}`);
      const data = await res.json();
      if (data.success) setTrends(data);
    } catch (err) {
      console.error('Error fetching trends:', err);
    }
  }, [country, periodDays]);

  const refreshAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchOverview(), fetchCategoryAnalytics(), fetchRoiMetrics(), fetchTrends()]);
    setLoading(false);
  }, [fetchOverview, fetchCategoryAnalytics, fetchRoiMetrics, fetchTrends]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  const StatCard = ({ title, value, icon: Icon, change, prefix = '', suffix = '' }) => (
    <Card data-testid={`stat-card-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">{title}</p>
            <p className="text-2xl font-bold mt-1">{prefix}{typeof value === 'number' ? value.toLocaleString() : value}{suffix}</p>
            {change !== undefined && (
              <div className={`flex items-center mt-1 text-sm ${change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {change >= 0 ? <ArrowUp className="w-3 h-3 mr-1" /> : <ArrowDown className="w-3 h-3 mr-1" />}
                {Math.abs(change)}% from last period
              </div>
            )}
          </div>
          <div className="p-3 bg-blue-100 rounded-full">
            <Icon className="w-6 h-6 text-blue-600" />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-6" data-testid="service-analytics-dashboard">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Service Analytics Dashboard</h1>
            <p className="text-gray-500 mt-1">Track service purchases, ROI, and trends</p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={country} onValueChange={setCountry}>
              <SelectTrigger className="w-32" data-testid="country-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="UK">🇬🇧 UK</SelectItem>
                <SelectItem value="SL">🇱🇰 Sri Lanka</SelectItem>
                <SelectItem value="US">🇺🇸 US</SelectItem>
              </SelectContent>
            </Select>
            <Select value={periodDays.toString()} onValueChange={(v) => setPeriodDays(parseInt(v))}>
              <SelectTrigger className="w-32" data-testid="period-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={refreshAll} disabled={loading} data-testid="refresh-btn">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid grid-cols-4 w-full max-w-xl">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="category" data-testid="tab-category">Category</TabsTrigger>
            <TabsTrigger value="roi" data-testid="tab-roi">ROI</TabsTrigger>
            <TabsTrigger value="trends" data-testid="tab-trends">Trends</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            {overview && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <StatCard title="Total Purchases" value={overview.overview.total_purchases} icon={ShoppingCart} />
                  <StatCard title="Total Revenue" value={overview.overview.total_revenue} icon={DollarSign} prefix="£" />
                  <StatCard title="This Month" value={overview.overview.revenue_this_month} icon={TrendingUp} prefix="£" />
                  <StatCard title="Avg Order Value" value={overview.overview.average_order_value} icon={Package} prefix="£" />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Revenue by Type */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Revenue by Purchase Type</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={250}>
                        <PieChart>
                          <Pie
                            data={Object.entries(overview.by_type).map(([type, data]) => ({
                              name: type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()),
                              value: data.revenue
                            }))}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={80}
                            paddingAngle={5}
                            dataKey="value"
                            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                          >
                            {Object.keys(overview.by_type).map((_, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value) => `£${value.toLocaleString()}`} />
                        </PieChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  {/* Top Categories */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Revenue by Category</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={250}>
                        <BarChart data={overview.by_category.slice(0, 8)} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" tickFormatter={(v) => `£${v}`} />
                          <YAxis dataKey="icon" type="category" width={40} />
                          <Tooltip formatter={(value) => `£${value.toLocaleString()}`} />
                          <Bar dataKey="revenue" fill="#8884d8" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </div>
              </>
            )}
          </TabsContent>

          {/* Category Tab */}
          <TabsContent value="category">
            <div className="mb-4">
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-64" data-testid="category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.icon} {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {categoryAnalytics && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <StatCard 
                    title="Total Purchases" 
                    value={categoryAnalytics.summary.total_purchases} 
                    icon={ShoppingCart} 
                  />
                  <StatCard 
                    title="Total Revenue" 
                    value={categoryAnalytics.summary.total_revenue} 
                    icon={DollarSign} 
                    prefix="£" 
                  />
                  <StatCard 
                    title="Candidate Revenue" 
                    value={categoryAnalytics.summary.candidate_revenue} 
                    icon={Users} 
                    prefix="£" 
                    suffix={` (${categoryAnalytics.summary.candidate_percentage}%)`}
                  />
                  <StatCard 
                    title="Avg Order Value" 
                    value={categoryAnalytics.summary.avg_order_value} 
                    icon={Package} 
                    prefix="£" 
                  />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                  {/* Daily Trend */}
                  <Card className="lg:col-span-2">
                    <CardHeader>
                      <CardTitle>{categoryAnalytics.category_icon} {categoryAnalytics.category_name} - Daily Trend</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={250}>
                        <LineChart data={categoryAnalytics.daily_trend}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} />
                          <YAxis yAxisId="left" />
                          <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => `£${v}`} />
                          <Tooltip />
                          <Legend />
                          <Line yAxisId="left" type="monotone" dataKey="purchases" stroke="#8884d8" name="Purchases" />
                          <Line yAxisId="right" type="monotone" dataKey="revenue" stroke="#82ca9d" name="Revenue (£)" />
                        </LineChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  {/* Services Breakdown */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Top Services</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {categoryAnalytics.services.slice(0, 5).map((service, i) => (
                          <div key={service.service_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center gap-3">
                              <span className="text-lg">{service.icon}</span>
                              <div>
                                <p className="font-medium text-sm">{service.name}</p>
                                <p className="text-xs text-gray-500">{service.purchases} purchases</p>
                              </div>
                            </div>
                            <span className="font-semibold text-green-600">£{service.revenue}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Bundles Breakdown */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Bundle Performance</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {categoryAnalytics.bundles.length > 0 ? (
                        <div className="space-y-3">
                          {categoryAnalytics.bundles.map((bundle) => (
                            <div key={bundle.bundle_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                              <div>
                                <p className="font-medium text-sm">{bundle.name}</p>
                                <p className="text-xs text-gray-500">{bundle.purchases} purchases • {bundle.includes.length} services</p>
                              </div>
                              <span className="font-semibold text-green-600">£{bundle.revenue}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-500 text-center py-8">No bundle purchases yet</p>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </>
            )}
          </TabsContent>

          {/* ROI Tab */}
          <TabsContent value="roi">
            <div className="mb-4">
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-64" data-testid="roi-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.icon} {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {roiMetrics && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <StatCard 
                    title="Total Revenue" 
                    value={roiMetrics.roi_metrics.total_revenue} 
                    icon={DollarSign} 
                    prefix="£" 
                  />
                  <StatCard 
                    title="Avg Per Purchase" 
                    value={roiMetrics.roi_metrics.avg_revenue_per_purchase} 
                    icon={Package} 
                    prefix="£" 
                  />
                  <StatCard 
                    title="Badges Awarded" 
                    value={roiMetrics.roi_metrics.badges_awarded} 
                    icon={Award} 
                  />
                  <StatCard 
                    title="Badge Conversion" 
                    value={roiMetrics.roi_metrics.badge_conversion_rate} 
                    icon={TrendingUp} 
                    suffix="%" 
                  />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Revenue Breakdown */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Revenue Breakdown</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={200}>
                        <PieChart>
                          <Pie
                            data={[
                              { name: 'Single Services', value: roiMetrics.revenue_breakdown.single_services.revenue },
                              { name: 'Bundles', value: roiMetrics.revenue_breakdown.bundles.revenue },
                              { name: 'Subscriptions', value: roiMetrics.revenue_breakdown.subscriptions.revenue },
                            ].filter(d => d.value > 0)}
                            cx="50%"
                            cy="50%"
                            innerRadius={40}
                            outerRadius={60}
                            paddingAngle={5}
                            dataKey="value"
                            label={({ name, value }) => `${name}: £${value}`}
                          >
                            <Cell fill="#8884d8" />
                            <Cell fill="#82ca9d" />
                            <Cell fill="#ffc658" />
                          </Pie>
                          <Tooltip formatter={(value) => `£${value}`} />
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="mt-4 space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full bg-[#8884d8]"></span>
                            Single Services
                          </span>
                          <span>£{roiMetrics.revenue_breakdown.single_services.revenue} ({roiMetrics.revenue_breakdown.single_services.count})</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full bg-[#82ca9d]"></span>
                            Bundles
                          </span>
                          <span>£{roiMetrics.revenue_breakdown.bundles.revenue} ({roiMetrics.revenue_breakdown.bundles.count})</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full bg-[#ffc658]"></span>
                            Subscriptions
                          </span>
                          <span>£{roiMetrics.revenue_breakdown.subscriptions.revenue} ({roiMetrics.revenue_breakdown.subscriptions.count})</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Top Services */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Top Services by Revenue</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {roiMetrics.top_services.length > 0 ? (
                          roiMetrics.top_services.map((service, i) => (
                            <div key={service.service_id} className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <span className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-xs font-medium">{i + 1}</span>
                                <span className="text-lg">{service.icon}</span>
                                <span className="text-sm truncate max-w-[120px]">{service.name}</span>
                              </div>
                              <span className="font-medium text-green-600">£{service.revenue}</span>
                            </div>
                          ))
                        ) : (
                          <p className="text-gray-500 text-center py-4">No purchases yet</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Top Bundles */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Top Bundles by Purchases</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {roiMetrics.top_bundles.length > 0 ? (
                          roiMetrics.top_bundles.map((bundle, i) => (
                            <div key={bundle.bundle_id} className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <span className="w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center text-xs font-medium">{i + 1}</span>
                                <span className="text-sm truncate max-w-[140px]">{bundle.name}</span>
                              </div>
                              <div className="text-right">
                                <span className="font-medium text-green-600">£{bundle.revenue}</span>
                                <p className="text-xs text-gray-500">{bundle.purchases} sold</p>
                              </div>
                            </div>
                          ))
                        ) : (
                          <p className="text-gray-500 text-center py-4">No bundles sold yet</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </>
            )}
          </TabsContent>

          {/* Trends Tab */}
          <TabsContent value="trends">
            {trends && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <StatCard 
                    title={`Purchases (${periodDays} days)`} 
                    value={trends.total_in_period} 
                    icon={ShoppingCart} 
                  />
                  <StatCard 
                    title={`Revenue (${periodDays} days)`} 
                    value={trends.revenue_in_period} 
                    icon={DollarSign} 
                    prefix="£" 
                  />
                </div>

                <Card className="mb-6">
                  <CardHeader>
                    <CardTitle>Daily Purchase & Revenue Trend</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={trends.daily_trend}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} />
                        <YAxis yAxisId="left" />
                        <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => `£${v}`} />
                        <Tooltip />
                        <Legend />
                        <Line yAxisId="left" type="monotone" dataKey="purchases" stroke="#8884d8" name="Purchases" strokeWidth={2} />
                        <Line yAxisId="right" type="monotone" dataKey="revenue" stroke="#82ca9d" name="Revenue (£)" strokeWidth={2} />
                      </LineChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Category Performance Comparison</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={Object.entries(trends.category_trends).map(([cat, data]) => ({
                        category: data.name,
                        icon: data.icon,
                        purchases: data.data.reduce((sum, d) => sum + d.purchases, 0),
                        revenue: data.data.reduce((sum, d) => sum + d.revenue, 0),
                      }))}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="icon" />
                        <YAxis yAxisId="left" />
                        <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => `£${v}`} />
                        <Tooltip content={({ payload, label }) => {
                          if (payload && payload.length) {
                            const data = payload[0].payload;
                            return (
                              <div className="bg-white p-3 border rounded shadow">
                                <p className="font-medium">{data.category}</p>
                                <p className="text-sm">Purchases: {data.purchases}</p>
                                <p className="text-sm">Revenue: £{data.revenue}</p>
                              </div>
                            );
                          }
                          return null;
                        }} />
                        <Legend />
                        <Bar yAxisId="left" dataKey="purchases" fill="#8884d8" name="Purchases" radius={[4, 4, 0, 0]} />
                        <Bar yAxisId="right" dataKey="revenue" fill="#82ca9d" name="Revenue (£)" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default ServiceAnalyticsDashboard;
